import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { viewStore } from "@/editor/store";
import { useStoreSubscribe } from "@/lib/store";
import { FlatLayerRow, type FlatLayerRowDropIndicator, type SidebarSelectionModifiers } from "./FlatLayerRow";
import {
  buildFlatRowOffsets,
  findFlatRowIndexAtOffset,
  flatRowHeight,
  flatRowOffsetTop,
  type FlatLayerRow as FlatLayerRowData,
  type LayerDropTarget,
} from "./layerList";

const VIRTUAL_OVERSCAN_ROWS = 8;

export interface VirtualLayerListProps {
  rows: FlatLayerRowData[];
  selectedNodeIds: string[];
  primarySelectedNodeId?: string;
  collapsedNodeIds: Set<string>;
  draggedNodeIds: string[];
  dropIndicator?: FlatLayerRowDropIndicator;
  editingNodeId?: string;
  scrollToNodeId?: string;
  onSelectNode: (nodeId: string, modifiers: SidebarSelectionModifiers) => void;
  onRenameNode: (nodeId: string, name: string) => void;
  onVisibilityChange: (nodeId: string, visible: boolean) => void;
  onLockedChange: (nodeId: string, locked: boolean) => void;
  onMoveNodes: (nodeIds: string[], target: LayerDropTarget) => void;
  onContextMenu: (nodeId: string, point: { x: number; y: number }) => void;
  onToggleCollapsed: (nodeId: string) => void;
  onDragStartNode: (nodeId: string, event: React.DragEvent<HTMLDivElement>) => void;
  onDragEndNode: () => void;
  onDragOverNode: (indicator: FlatLayerRowDropIndicator | undefined) => void;
  onDropNode: (target: LayerDropTarget) => void;
  isValidDropTarget: (target: LayerDropTarget) => boolean;
  onEditNode: (nodeId: string | undefined) => void;
  onRowElement: (nodeId: string, element: HTMLDivElement | null) => void;
  onScrollToNodeComplete?: () => void;
  allowScripts?: boolean;
  onEditScript?: (nodeId: string) => void;
  emptyPageDrop?: {
    parentId: string;
    onDrop: () => void;
  };
}

export function VirtualLayerList({
  rows,
  selectedNodeIds,
  primarySelectedNodeId,
  collapsedNodeIds,
  draggedNodeIds,
  dropIndicator,
  editingNodeId,
  scrollToNodeId,
  onSelectNode,
  onRenameNode,
  onVisibilityChange,
  onLockedChange,
  onMoveNodes,
  onContextMenu,
  onToggleCollapsed,
  onDragStartNode,
  onDragEndNode,
  onDragOverNode,
  onDropNode,
  isValidDropTarget,
  onEditNode,
  onRowElement,
  onScrollToNodeComplete,
  allowScripts = false,
  onEditScript,
  emptyPageDrop,
}: VirtualLayerListProps) {
  const hoveredNodeId = useStoreSubscribe(viewStore.hoveredNodeId);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);

  const rowOffsets = useMemo(() => buildFlatRowOffsets(rows), [rows]);
  const totalHeight = rowOffsets[rowOffsets.length - 1] ?? 0;
  const selectedSet = useMemo(() => new Set(selectedNodeIds), [selectedNodeIds]);
  const draggedSet = useMemo(() => new Set(draggedNodeIds), [draggedNodeIds]);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    const updateViewport = () => setViewportHeight(element.clientHeight);
    updateViewport();
    const observer = new ResizeObserver(updateViewport);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!scrollToNodeId) {
      return;
    }

    const index = rows.findIndex(row => row.kind === "node" && row.node.id === scrollToNodeId);
    if (index < 0) {
      return;
    }

    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const rowTop = flatRowOffsetTop(rowOffsets, index);
    const rowHeight = flatRowHeight(rows[index]!);
    const targetScrollTop = rowTop - (container.clientHeight - rowHeight) / 2;
    container.scrollTop = Math.max(0, targetScrollTop);
    setScrollTop(container.scrollTop);
    onScrollToNodeComplete?.();
  }, [onScrollToNodeComplete, rowOffsets, rows, scrollToNodeId]);

  const visibleRange = useMemo(() => {
    if (rows.length === 0) {
      return { start: 0, end: -1 };
    }

    const start = Math.max(0, findFlatRowIndexAtOffset(rowOffsets, scrollTop) - VIRTUAL_OVERSCAN_ROWS);
    const end = Math.min(
      rows.length - 1,
      findFlatRowIndexAtOffset(rowOffsets, scrollTop + Math.max(viewportHeight, 1)) + VIRTUAL_OVERSCAN_ROWS,
    );
    return { start, end };
  }, [rowOffsets, rows.length, scrollTop, viewportHeight]);

  const visibleIndices = useMemo(() => {
    const indices: number[] = [];
    for (let index = visibleRange.start; index <= visibleRange.end; index += 1) {
      indices.push(index);
    }
    return indices;
  }, [visibleRange.end, visibleRange.start]);

  return (
    <div
      ref={scrollRef}
      className="min-h-0 flex-1 overflow-auto overscroll-contain p-2"
      onScroll={event => setScrollTop(event.currentTarget.scrollTop)}
    >
      <section>
        <div className="relative rounded bg-white/[0.04]">
          <div style={{ height: totalHeight }}>
            {visibleIndices.map(index => {
              const row = rows[index]!;
              const top = flatRowOffsetTop(rowOffsets, index);
              const height = flatRowHeight(row);
              const key = row.kind === "node" ? row.node.id : `empty-${row.parentId}`;

              if (row.kind === "node") {
                const selected = selectedSet.has(row.node.id);
                const canvasHovered = !selected
                  && hoveredNodeId === row.node.id
                  && !(row.depth === 0 && row.node.type === "frame");
                return (
                  <div
                    key={key}
                    className="absolute left-0 right-0"
                    style={{ top, height }}
                  >
                    <FlatLayerRow
                      kind="node"
                      node={row.node}
                      depth={row.depth}
                      index={row.index}
                      maskDecoration={row.maskDecoration}
                      parentId={row.parentId}
                      parentType={row.parentType}
                      selected={selected}
                      primary={primarySelectedNodeId === row.node.id}
                      canvasHovered={canvasHovered}
                      collapsed={collapsedNodeIds.has(row.node.id)}
                      dragging={draggedSet.has(row.node.id)}
                      dropPlacement={dropIndicator?.nodeId === row.node.id ? dropIndicator.placement : undefined}
                      editing={editingNodeId === row.node.id}
                      onSelectNode={onSelectNode}
                      onRenameNode={onRenameNode}
                      onVisibilityChange={onVisibilityChange}
                      onLockedChange={onLockedChange}
                      onContextMenu={onContextMenu}
                      onToggleCollapsed={onToggleCollapsed}
                      onDragStartNode={onDragStartNode}
                      onDragEndNode={onDragEndNode}
                      onDragOverNode={onDragOverNode}
                      onDropNode={onDropNode}
                      isValidDropTarget={isValidDropTarget}
                      onEditNode={onEditNode}
                      onRowElement={onRowElement}
                      draggedNodeIds={draggedNodeIds}
                      allowScripts={allowScripts}
                      onEditScript={onEditScript}
                    />
                  </div>
                );
              }

              return (
                <div
                  key={key}
                  className="absolute left-0 right-0"
                  style={{ top, height }}
                >
                  <FlatLayerRow
                    kind="empty-drop"
                    depth={row.depth}
                    parentId={row.parentId}
                    parentType={row.parentType}
                    onMoveNodes={onMoveNodes}
                    onDragEndNode={onDragEndNode}
                    isValidDropTarget={isValidDropTarget}
                    draggedNodeIds={draggedNodeIds}
                  />
                </div>
              );
            })}
          </div>
          {emptyPageDrop ? (
            <div
              className="mx-2 my-1 h-6 rounded border border-dashed border-white/10"
              onDragOver={event => {
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
              }}
              onDrop={event => {
                event.preventDefault();
                emptyPageDrop.onDrop();
              }}
            />
          ) : null}
        </div>
      </section>
    </div>
  );
}
